import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { type NavItem } from '@/lib/layout.config';
import {
  navGroupOpened,
  navGroupToggled,
  selectOpenNavGroups,
} from '@/shared/store/reducers/preferences/preferences.slice';
import { useAppDispatch, useAppSelector } from '@/shared/store/typed-hooks/typed-hooks';
import { resolveActiveNavPath } from '@/shared/utils/resolve-active-nav-path/resolve-active-nav-path.util';

export interface SidebarNavGroupLogic {
  isOpen: boolean;
  /** The one child that is the current page, if any — never two. */
  activeChildTo: string | undefined;
  toggle: () => void;
}

/**
 * A group is closed unless the operator opened it — and the one that holds the
 * current page opens itself on arrival, so a bookmark into `/venues/:id` never
 * lands on a sidebar with every group shut and no sign of where the page lives.
 *
 * The route-driven open is keyed on the pathname, not on `isOpen`: an operator
 * who closes the group while standing inside it has said something, and it
 * stays closed until they navigate again. Both sidebar hosts run this effect
 * for the same group, which is why the action is idempotent.
 */
export function useSidebarNavGroupLogic(
  item: NavItem,
  children: readonly NavItem[],
): SidebarNavGroupLogic {
  const dispatch = useAppDispatch();
  const openGroups = useAppSelector(selectOpenNavGroups);
  const { pathname } = useLocation();

  const activeChildTo = useMemo(
    () =>
      resolveActiveNavPath(
        children.map((child) => child.to),
        pathname,
      ),
    [children, pathname],
  );
  const hasActiveChild = activeChildTo !== undefined;

  useEffect(() => {
    if (hasActiveChild) {
      dispatch(navGroupOpened(item.labelKey));
    }
  }, [dispatch, hasActiveChild, item.labelKey, pathname]);

  const toggle = useCallback(() => {
    dispatch(navGroupToggled(item.labelKey));
  }, [dispatch, item.labelKey]);

  return { isOpen: openGroups.includes(item.labelKey), activeChildTo, toggle };
}
